import Modal, {ModalFooter} from '../shared/Modal.js';
import Button from '../shared/Button.js';
// ============================================================================
// src/pig/BreedingView.tsx  —  Phase 2 Round 6
// ----------------------------------------------------------------------------
// Breeding Gantt view for pig cycles.
//
// ITS THREE DATA READS AND ITS WRITE ARE ALL ITS OWN NOW.
//
// The cycle list, the boar names and the breeder registry used to arrive
// through usePig() as App's boot app_store results. `pig.breeding.core` owns
// all three, which is what lets `breeding` retire from the frozen app_store
// consumer list in tests/fixtures/former-eager-dependencies.json.
//
// THE SAVE MOVED TOO, AND THAT IS WHAT MADE THE SURFACE PERMANENT. It used to
// be `persistBreeding`, an App prop threaded through PigEntry whose whole body
// was one app_store upsert, called beside PigContext's raw `setBreedingCycles`
// so a mirror stayed in step. Two facts retired both halves: the write needed
// App only for the retry/status helper, which now lives in
// src/lib/appStoreBlobApi.ts and is called here directly; and the mirror had no
// reader left — nothing outside App destructured the cycle list off usePig(),
// and App's own read was the dead `resolveSire` closure. So this page publishes
// its next array locally (savedCycles, below) and writes the blob itself, and
// src/app/data/migratedSurfaces/view.breeding.ts declares an empty residual.
// (The residual id is spelled out there rather than here on purpose: this file
// may not name it in full, because
// tests/static/activity_change_logging_static.test.js refuses a fabricated
// `pig` + `.breeding` Activity entity and enforces the refusal on this file's
// source text — allowing only the `.core` route-resource id.)
//
// THE 500 ms AUTOSAVE HANDLE IS AT MODULE SCOPE, DELIBERATELY. App hoisted this
// ref, so the handle outlived every unmount of this page: open the cycle modal,
// edit, navigate away inside the debounce window and back, and the next edit
// CLEARED the pending save instead of racing it. `showBreedForm` lives in
// PigContext, so that modal really does survive a remount. A `useRef` here
// would hand the remount a fresh null and orphan the pending timer, which is a
// behaviour change; module scope reproduces the lifetime App gave it.
//
// The form state (breedForm, showBreedForm, editBreedId, breedTlStart) also
// still comes from usePig, and deliberately: it is UI state the provider
// happens to host, not one of the former-eager boot reads, so it is invisible
// to the retirement above and moving it is a different change.
// ============================================================================
import React from 'react';
import {openableProps} from '../shared/openable.js';
import {fmtMDY, todayISO, addDays, toISO} from '../lib/dateUtils.js';
import {S} from '../lib/styles.js';
import {
  calcBreedingTimeline,
  calcCycleStatus,
  buildCycleSeqMap,
  cycleLabel,
  PIG_GROUPS,
  PIG_GROUP_COLORS,
} from '../lib/pig.js';
import {DEFAULT_BOAR_NAMES} from '../lib/pig.js';
import {programDotStyle} from '../lib/programColors.js';
// The `no-unused-vars` disable this import used to carry is deleted, not
// retargeted: the core rule is off for TypeScript files and
// @typescript-eslint/no-unused-vars resolves JSX references, so the directive
// itself reported as unused.
import Badge from '../shared/Badge.jsx';
import InlineNotice from '../shared/InlineNotice.jsx';
import {sb} from '../lib/supabase.js';
import {expectedFrom, newPigRequestId, pigRejectionMessage, type PigOpOutcome} from '../lib/pigOperationsApi.js';
import {createRecordOpRunner, sameRecordValue} from './pigRecordOps.js';
import PigConflictNotice, {type PigConflictState} from './PigConflictNotice.js';
import {
  cycleDeleteBusy,
  cycleEditForm,
  cycleSaveVerdict,
  deleteCycleAfterSettle,
  openCycleEditor,
  type CycleDeleteGate,
} from './breedCycleEditor.js';
import {useAuth} from '../contexts/AuthContext.jsx';
import {useConfirm} from '../contexts/ConfirmContext.jsx';
import {usePig} from '../contexts/PigContext.jsx';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {PigBreedingCoreData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import {useConfirmedDraftAutosave} from '../shared/useConfirmedDraftAutosave.js';
import type {PigBreederRow, PigBreedingCycle, PigBreedingTimeline} from '../lib/pig.js';
import {pigBreedingTimelineRange} from '../lib/pigBreedingTimelineRange.js';
import Header from '../shared/Header.tsx';
import {COARSE_POINTER_QUERY, usePhoneLayout} from '../shared/usePhoneLayout.js';
import './breedingRedesign.css';

// ── No props ────────────────────────────────────────────────────────────────
// src/pig/PigEntry.tsx mounts this view with none. The two it used to pass —
// `persistBreeding` and `breedAutoSaveTimer` — were App's, and both are this
// file's now. The write itself is no longer a whole-document save: every cycle
// change is one pig_apply_operation (migration 235), below.

// E4 residual: the old module-scope 500 ms debounce handle is gone — the cycle
// editor autosaves through the shared confirmed-draft controller now, which
// owns the debounce, keeps the newest draft until its exact revision is
// CONFIRMED, refuses to let a refused save look like a saved one, and wires
// the navigation guard.

// ── Local shapes ────────────────────────────────────────────────────────────

// The breeding-cycle form as THIS view uses it. PigContext seeds the state from
// a five-key literal (group / boar1Tags / boar2Tags / exposureStart / notes), so
// its inferred type is that seed; every opener here — + Add Cycle, the Gantt bar
// click, the cycle card — replaces it with the nine-key shape below, and the
// modal reads all nine. Declared here because the wider shape is this view's,
// not the context's: PigContext never reads a field off it.
type BreedForm = {
  group: string;
  customSuffix: string;
  boar1Tags: string;
  boar2Tags: string;
  // Sow tags the admin removed from the cycle, so the auto-merge stops
  // re-adding them. Read only through Array.isArray + includes/filter.
  excludedSows: string[];
  exposureStart: string;
  notes: string;
  boar1Name: string;
  boar2Name: string;
};

// The two tag fields removeSow / addSow address by name.
type BoarTagKey = 'boar1Tags' | 'boar2Tags';

// A breeder row as the sow pool reads it. src/lib/pig.ts's PigBreederRow names
// sex / archived / transferredFromBatch and leaves everything else to its index
// signature; the pool also reads the tag and the group.
type BreedingSow = PigBreederRow & {tag?: string | null; group?: string | number | null};

// One Gantt row. `startKey`/`endKey` index the timeline calcBreedingTimeline
// returns, and `phase` indexes the per-group colour table, so both are the key
// unions of those objects rather than plain strings — that is what makes the
// two lookups in the bar renderer legal without a cast.
type PigPhaseKey = keyof (typeof PIG_GROUP_COLORS)[keyof typeof PIG_GROUP_COLORS];
type PhaseRow = {
  group: string;
  phase: PigPhaseKey;
  label: string;
  startKey: keyof PigBreedingTimeline;
  endKey: keyof PigBreedingTimeline;
};

// PIG_GROUPS holds the group ids as STRINGS ('1','2','3'); PIG_GROUP_COLORS, in
// the same module, is keyed by the NUMERIC literals 1|2|3. JavaScript property
// access coerces the two and the Gantt has always relied on that. Both
// declarations live in src/lib/pig.ts, outside this cluster, so the coercion is
// recorded here as a string-keyed view of the same object.

// A stored breeding cycle as this view reads it. src/lib/pig.ts's
// PigBreedingCycle names the fields its own helpers use; persistBreedCycle
// below also spreads the whole calcBreedingTimeline result into the stored row,
// which is what makes `calcBreedingTimeline(c.exposureStart) || c` a working
// fallback — Partial<PigBreedingTimeline> records that. `excludedSows` and
// `notes` are this view's own columns on the same row.
type BreedingCycleRow = PigBreedingCycle &
  Partial<PigBreedingTimeline> & {
    excludedSows?: string[] | null;
    notes?: string | null;
    // The two per-boar tallies persistBreedCycle writes as `parseTags(...).length`.
    // Declared here beside the other columns this view owns, because
    // PigBreedingCycle leaves them to its index signature — and an `unknown`
    // rendered as a JSX child only type-checked while a SIBLING child in the
    // same element was `any`: `boarNames` came out of the provider as
    // `JSON.parse`'s `any`, which collapsed the whole children union. Reading
    // the same value through a typed resource removes the `any` and surfaces
    // what was always there. Nothing about the render changed.
    boar1Count?: number | string | null;
    boar2Count?: number | string | null;
  };

// The floating Gantt tooltip. `type` is checked against 'pig' before every read
// because this same tooltip shape is used by the cattle timeline.
type BreedTooltip = {
  type: string;
  group: PigBreedingCycle['group'];
  cycleLbl: string;
  phase: string;
  phaseName: string;
  start: string;
  end: string;
  sowCount: PigBreedingCycle['sowCount'];
  vx: number;
  vy: number;
};

const PIG_BREEDING_PARAMS = Object.freeze({});

export default function BreedingView() {
  const {confirmDelete} = useConfirm();
  // The header's Saving / Saved / Error indicator. It was App's `sbSave` that
  // drove it; the extracted writer takes the sink so the signal is identical.
  const {setSaveStatus} = useAuth();
  const [tooltip, setTooltip] = React.useState<BreedTooltip | null>(null);
  // ONLY THE UI STATE COMES FROM usePig NOW. The three former-eager arrays that
  // used to be destructured here — breedingCycles, boarNames, breeders — are
  // read through pig.breeding.core below, and `setBreedingCycles` went with the
  // write: it kept a mirror in step for readers that no longer exist. Nothing
  // in src/ destructures the cycle list off usePig() any more, and App's own
  // read was the `resolveSire` closure both of its consumers stopped calling.
  // What is left here is form state the provider happens to host, seeded from
  // module-scope literals rather than from any boot read.
  const {
    breedTlStart,
    setBreedTlStart,
    breedForm: breedFormRaw,
    setBreedForm: setBreedFormRaw,
    showBreedForm,
    setShowBreedForm,
    editBreedId: editBreedIdRaw,
    setEditBreedId,
  } = usePig();
  const breedingResource = useRouteResource(RESOURCE.PIG_BREEDING_CORE, PIG_BREEDING_PARAMS);
  const breedingData = breedingResource.data as PigBreedingCoreData | null;
  // THE OPTIMISTIC OVERRIDE. App's context write used to repaint this Gantt
  // synchronously, because the context WAS this view's data source; a query
  // result cannot. So a save publishes its next array here, and once it has, it
  // wins for the rest of this mount — which is exactly what the context value
  // did, including the part where a failed app_store write is not rolled back.
  // A remount re-reads from the server, so the divergence cannot outlive the
  // page. It is now the ONLY publish: the context mirror it used to sit beside
  // had no reader left.
  const [savedCycles, setSavedCycles] = React.useState<BreedingCycleRow[] | null>(null);
  // The one place this view states what it believes the two opaque arrays hold.
  // src/lib/pig.ts declares both shapes already.
  const breedingCycles = savedCycles ?? ((breedingData?.breedingCycles || []) as BreedingCycleRow[]);
  const breeders = (breedingData?.breeders || []) as BreedingSow[];
  // Never null and never partial: pig.breeding.core spreads the stored blob
  // onto DEFAULT_BOAR_NAMES, which is the seed PigContext used to hand over for
  // a farm with no ppp-boars-v1 row.
  const boarNames = breedingData?.boarNames ?? DEFAULT_BOAR_NAMES;
  // E4 confirmed persistence, now through pig_apply_operation (235): the local
  // overlay publishes ONLY the server's confirmed row (the runner adopts it),
  // so a refused write never repaints the timeline. The server derives the
  // roster counts, the timeline and the default boar names, keeps the cycles
  // ordered, refuses a group change, and refuses a delete while farrowing
  // records or a farm-born batch still point at the cycle.
  const cyclesRef = React.useRef<BreedingCycleRow[]>([]);
  cyclesRef.current = breedingCycles;
  const breedingResourceRef = React.useRef(breedingResource);
  breedingResourceRef.current = breedingResource;
  const [breedNotice, setBreedNotice] = React.useState<{kind: 'error'; message: string} | null>(null);
  const [breedConflict, setBreedConflict] = React.useState<PigConflictState | null>(null);
  const [applyCycleOp] = React.useState(() =>
    createRecordOpRunner<BreedingCycleRow>({
      sb,
      rowKey: 'cycle',
      deletedKey: 'cycle_id',
      getRows: () => cyclesRef.current,
      setRows: (next) => {
        const sorted = [...next].sort((a, b) =>
          String(a.exposureStart || '').localeCompare(String(b.exposureStart || '')),
        );
        cyclesRef.current = sorted;
        setSavedCycles(sorted);
      },
      reread: () => void breedingResourceRef.current.refetch(),
      onSaveStatus: setSaveStatus,
    }),
  );
  // Narrowed to the nine-key shape this view writes and reads (see BreedForm).
  // The setter is re-typed with it for the same reason: the context's inferred
  // parameter type is its five-key seed, and every setBreedForm call in this
  // file passes the wider object it has always passed.
  // The open cycle's id. PigContext types it `unknown` — it stores whatever id
  // a view hands it — and this view only ever sets it to a cycle's id or to the
  // `String(Date.now())` persistBreedCycle mints, so `string | null` is what it
  // holds here.
  const editBreedId = editBreedIdRaw as string | null;
  const breedForm = breedFormRaw as BreedForm;
  const setBreedForm = setBreedFormRaw as unknown as React.Dispatch<React.SetStateAction<BreedForm>>;
  const timelineToday = todayISO();
  const timelineRange = pigBreedingTimelineRange(timelineToday, breedTlStart);
  const btlS = new Date(timelineRange.start + 'T12:00:00');
  const gridWidth = (timelineRange.totalDays / 7) * 40;
  const isCoarsePointer = usePhoneLayout(COARSE_POINTER_QUERY);
  const ganttRowHeight = isCoarsePointer ? 40 : 32;
  function bpct(iso: string) {
    return ((Date.parse(iso) - Date.parse(timelineRange.start)) / 86400000 / timelineRange.totalDays) * 100;
  }
  const bWkHdrs = Array.from({length: timelineRange.weeks}, (_, i) => addDays(btlS, i * 7));
  const ganttRef = React.useRef<HTMLDivElement | null>(null);
  const [todayScrollRequest, setTodayScrollRequest] = React.useState(0);
  const todayPosition = (bpct(timelineToday) / 100) * gridWidth;
  React.useLayoutEffect(() => {
    const element = ganttRef.current;
    if (!element) return;
    // Keep the current work near the left of the plot while the full thirteen
    // months remain reachable by horizontal scrolling, including the keyboard.
    const visiblePlotWidth = Math.max(0, element.clientWidth - 210);
    element.scrollLeft = Math.max(0, todayPosition - visiblePlotWidth * 0.12);
  }, [todayPosition, todayScrollRequest]);
  const cycleSeqMap = buildCycleSeqMap(breedingCycles);

  const EMPTY_BREED = {
    group: '1',
    customSuffix: '',
    boar1Tags: '',
    boar2Tags: '',
    excludedSows: [],
    exposureStart: '',
    notes: '',
    boar1Name: boarNames.boar1,
    boar2Name: boarNames.boar2,
  };

  // Breeder-registry-driven sow pool: any Sow/Gilt in the breeders tab
  // whose group matches and who isn't archived.
  function sowsForGroup(group: string | number | null | undefined) {
    return breeders
      .filter((b) => !b.archived && (b.sex === 'Sow' || b.sex === 'Gilt') && String(b.group || '') === String(group))
      .map((b) => b.tag)
      .filter(Boolean) as string[];
  }
  // Add any group sows that aren't already listed under either boar to
  // Boar 1, EXCEPT sows the admin previously removed (excludedSows).
  // Returns the new boar1Tags string (newline-separated).
  function mergeSowsIntoB1(
    group: string | number | null | undefined,
    curB1: string | null | undefined,
    curB2: string | null | undefined,
    excluded: readonly string[] | null | undefined,
  ) {
    const have1 = (curB1 || '')
      .split('\n')
      .map((t) => t.trim())
      .filter(Boolean);
    const have2 = (curB2 || '')
      .split('\n')
      .map((t) => t.trim())
      .filter(Boolean);
    const haveSet = new Set([...have1, ...have2]);
    const excludedSet = new Set(Array.isArray(excluded) ? excluded.map(String) : []);
    const missing = sowsForGroup(group).filter((t) => !haveSet.has(t) && !excludedSet.has(String(t)));
    if (missing.length === 0) return have1.join('\n');
    return [...have1, ...missing].join('\n');
  }
  // Cycle is "locked" once 14 days have passed since exposureStart —
  // Ronnie's 2-week grace period to shuffle sow/boar assignments after
  // the cycle goes live. Before lock, admin can still add/remove sows.
  function isCycleLocked(exposureStart: string | null | undefined) {
    if (!exposureStart) return false;
    const lockDay = toISO(addDays(new Date(exposureStart + 'T12:00:00'), 14));
    return todayISO() >= lockDay;
  }

  // `cycleId` was `unknown` while the array it wrote into was PigContext's
  // `unknown[]`. All three callers pass `editBreedId`, which this file has
  // always narrowed to `string | null`, and the array is now typed — so the
  // parameter states what it already received. No call site changed.
  // E4: returns the write's verdict so the controller and the close/delete
  // paths only act on a CONFIRMED save; the refusals are verdicts, not silent
  // returns. 235: one cycle id per open form (a fast second edit cannot create
  // a second cycle); once the server's row is adopted, later saves are
  // breeding.update of only the fields that changed, against the persisted row.
  const formCycleId = React.useRef<string | null>(null);
  const cycleRequest = React.useRef<{intent: string; requestId: string} | null>(null);
  const breedFormRef = React.useRef(breedForm);
  breedFormRef.current = breedForm;
  const CYCLE_UPDATE_FIELDS = [
    'customSuffix',
    'boar1Tags',
    'boar2Tags',
    'excludedSows',
    'exposureStart',
    'notes',
    'boar1Name',
    'boar2Name',
  ];
  function cycleFailure(outcome: PigOpOutcome): string | null {
    if (outcome.status === 'rejected') return 'Not saved: ' + pigRejectionMessage(outcome);
    if (outcome.status === 'unknown') return 'Not saved: ' + outcome.message;
    return null;
  }
  async function persistBreedCycle(formSnapshot: BreedForm, cycleId: string | null): Promise<boolean> {
    if (!formSnapshot.exposureStart) return false;
    const id = cycleId || (formCycleId.current ??= 'bc-' + newPigRequestId().replace(/^pig-/, ''));
    const existing = cyclesRef.current.find((c) => c.id === id) as Record<string, unknown> | undefined;
    if (cycleId && !existing) {
      setSaveStatus('error');
      return false;
    }
    const form = formSnapshot as unknown as Record<string, unknown>;
    let request;
    if (!existing) {
      const change = {group: form.group, ...Object.fromEntries(CYCLE_UPDATE_FIELDS.map((f) => [f, form[f] ?? '']))};
      request = {operation: 'breeding.create' as const, target: {cycle_id: id}, change};
    } else {
      // A saved cycle keeps its group and identity (the server refuses either).
      if (String(existing.group) !== String(form.group)) {
        setSaveStatus('error');
        return false;
      }
      const fields = CYCLE_UPDATE_FIELDS.filter((f) => !sameRecordValue(existing[f], form[f]));
      if (fields.length === 0) return true;
      request = {
        operation: 'breeding.update' as const,
        target: {cycle_id: id},
        expected: expectedFrom(existing, fields),
        change: Object.fromEntries(fields.map((f) => [f, form[f] ?? ''])),
      };
    }
    const intent = JSON.stringify(request);
    const prior = cycleRequest.current;
    const requestId = prior && prior.intent === intent ? prior.requestId : newPigRequestId();
    cycleRequest.current = {intent, requestId};
    const outcome = await applyCycleOp({requestId, ...request});
    if (outcome.status === 'saved' || outcome.status === 'no_change') {
      cycleRequest.current = null;
      setBreedNotice(null);
      setBreedConflict(null);
      // The modal adopts a freshly created cycle's id only once the create is
      // confirmed — an unsaved id would point the editor at nothing.
      if (!editBreedId) setEditBreedId(id);
      return true;
    }
    if (outcome.status === 'conflict') {
      cycleRequest.current = null;
      const conflicts = outcome.conflicts;
      setBreedConflict({
        conflicts,
        useLatest: async () => {
          void breedingResourceRef.current.refetch();
          setBreedConflict(null);
          const merged = {...breedFormRef.current} as Record<string, unknown>;
          for (const c of conflicts) merged[c.field] = c.current == null ? '' : c.current;
          const next = merged as unknown as BreedForm;
          setBreedForm(next);
          breedDraft.update(next);
        },
      });
      return false;
    }
    setBreedNotice({kind: 'error', message: cycleFailure(outcome) || 'Not saved.'});
    return false;
  }
  // A delete is refused while farrowing records or a farm-born batch still
  // point at the cycle; the notice names them so they can be removed first.
  // Returns true only for a CONFIRMED delete.
  async function deleteBreedCycle(id: string): Promise<boolean> {
    const outcome = await applyCycleOp({
      requestId: newPigRequestId(),
      operation: 'breeding.delete',
      target: {cycle_id: id},
    });
    if (outcome.status === 'refused') {
      const farrowing = outcome.links.farrowing_ids?.length || 0;
      const batches = outcome.links.feeder_ids?.length || 0;
      const parts = [
        farrowing ? `${farrowing} farrowing record${farrowing === 1 ? '' : 's'}` : '',
        batches ? `${batches} farm-born batch${batches === 1 ? '' : 'es'}` : '',
      ].filter(Boolean);
      setBreedNotice({
        kind: 'error',
        message: `Not deleted: ${parts.join(' and ')} still belong to this cycle. Remove them first.`,
      });
      return false;
    }
    const failed = cycleFailure(outcome);
    if (failed) {
      setBreedNotice({kind: 'error', message: failed.replace('Not saved', 'Not deleted')});
      return false;
    }
    return true;
  }
  // E4: the shared confirmed-draft controller owns the debounce (was a
  // module-scope 500 ms timer), the exposureStart gate (was an early return),
  // and the verdict — a refused save holds the draft here instead of closing
  // over it.
  // F12: a delete settles the draft first and gates this cycle's saves while
  // it is out (src/pig/breedCycleEditor.ts) — a deleted cycle never comes back.
  const cycleDeleteGate = React.useRef<CycleDeleteGate>(null);
  const {controller: breedDraft, state: breedSaveState} = useConfirmedDraftAutosave<BreedForm>({
    // The owner string deliberately avoids the reserved breeding entity-type
    // token — the activity guard allows only the `.core` resource name in this
    // file (breeding_pigs skip rule).
    ownerKey: 'breeding.cycle.editor',
    debounceMs: 500,
    validate: function (draft) {
      return Boolean(draft.exposureStart);
    },
    persist: function (draft) {
      const verdict = cycleSaveVerdict(cycleDeleteGate.current, editBreedId);
      if (verdict === 'drop') return Promise.resolve(true);
      if (verdict === 'refuse') return Promise.resolve(false);
      return persistBreedCycle(draft, editBreedId);
    },
    isBusy: function () {
      return cycleDeleteBusy(cycleDeleteGate.current);
    },
  });
  // F13 + F14: both openers (Gantt bar, cycle card) settle a held draft
  // BEFORE switching the form or edit id, and queue a re-pulled roster so the
  // roster the editor shows is the one close saves.
  function openSavedCycle(c: PigBreedingCycle) {
    openCycleEditor(
      breedDraft,
      () => {
        const {form, rosterChanged} = cycleEditForm(c, {
          locked: isCycleLocked(c.exposureStart),
          mergeRoster: mergeSowsIntoB1,
          boarNames: boarNames,
        });
        setBreedForm(form);
        setEditBreedId(c.id);
        if (rosterChanged) breedDraft.update(form);
      },
      () => setShowBreedForm(true),
    );
  }
  function updBreed(k: keyof BreedForm, v: string) {
    if (k === 'group' && editBreedId) return;
    var next = {...breedForm, [k]: v};
    // Group selection is only available while creating a cycle. Start a fresh
    // roster from that group's sows, including for a backdated new cycle.
    if (k === 'group' && v !== breedForm.group) {
      next.excludedSows = [];
      next.boar1Tags = mergeSowsIntoB1(v, '', '', []);
      next.boar2Tags = '';
    }
    setBreedForm(next);
    if (next.exposureStart) breedDraft.update(next);
  }
  async function closeBreedForm() {
    // A named autosave modal closes only over CONFIRMED work: a refused flush
    // leaves the draft and the modal right here (the failed strip shows).
    if (!(await breedDraft.flush())) return;
    formCycleId.current = null;
    setBreedNotice(null);
    setBreedConflict(null);
    setShowBreedForm(false);
    setEditBreedId(null);
  }

  // Phase row definitions per group
  const phaseRows: PhaseRow[] = PIG_GROUPS.flatMap((g) => [
    {group: g, phase: 'boar', label: `G${g} — Sows in with Boars`, startKey: 'boarStart', endKey: 'boarEnd'},
    {
      group: g,
      phase: 'paddock',
      label: `G${g} — Sows in Exposed Paddock`,
      startKey: 'paddockStart',
      endKey: 'paddockEnd',
    },
    {group: g, phase: 'farrowing', label: `G${g} — Farrowing`, startKey: 'farrowingStart', endKey: 'farrowingEnd'},
    {group: g, phase: 'weaning', label: `G${g} — Weaning`, startKey: 'weaningStart', endKey: 'weaningEnd'},
    {group: g, phase: 'gilt', label: `G${g} — Gilts Grow Out`, startKey: 'growStart', endKey: 'growEnd'},
    {group: g, phase: 'boarGrow', label: `G${g} — Boars Grow Out`, startKey: 'growStart', endKey: 'growEnd'},
  ]);

  return (
    <div>
      <Header />
      <header style={{maxWidth: 1180, margin: '0 auto', padding: '20px 16px 0'}}>
        <h1 style={{fontSize: 30, fontWeight: 800, margin: 0}}>Timeline</h1>
        <p style={{color: 'var(--text-2)'}}>Breeding cycles and upcoming milestones</p>
      </header>
      {showBreedForm && (
        <Modal
          title={editBreedId ? 'Breeding Cycle' : 'Add Breeding Cycle'}
          size="lg"
          autosave
          onClose={() => void closeBreedForm()}
          footer={
            <ModalFooter
              onDelete={
                editBreedId
                  ? () => {
                      confirmDelete('Delete this breeding cycle? This cannot be undone.', async () => {
                        // E4: the modal closes only over a CONFIRMED delete —
                        // a refused write leaves the cycle and the modal as
                        // they were, with the header Error showing.
                        const id = editBreedId;
                        const deleted = await deleteCycleAfterSettle({
                          id,
                          gate: cycleDeleteGate,
                          flush: () => breedDraft.flush(),
                          remove: () => deleteBreedCycle(id),
                        });
                        if (deleted) {
                          formCycleId.current = null;
                          setShowBreedForm(false);
                          setEditBreedId(null);
                        }
                      });
                    }
                  : undefined
              }
            />
          }
        >
          <div style={{padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10}}>
            {breedSaveState === 'failed' && (
              <InlineNotice
                notice={{kind: 'error', message: 'Changes have not been fully saved. Your draft is still here.'}}
                action={
                  <Button variant="secondary" onClick={() => void breedDraft.flush()}>
                    Retry Save
                  </Button>
                }
              />
            )}
            <InlineNotice notice={breedNotice} onDismiss={() => setBreedNotice(null)} />
            <PigConflictNotice conflict={breedConflict} />
            {/* Boar names (per cycle) */}
            <div
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '10px 14px',
              }}
            >
              <div style={{fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8}}>
                Boar names for this cycle
              </div>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10}}>
                <div>
                  <label style={S.label}>Boar 1 name</label>
                  <input value={breedForm.boar1Name || ''} onChange={(e) => updBreed('boar1Name', e.target.value)} />
                </div>
                <div>
                  <label style={S.label}>Boar 2 name</label>
                  <input value={breedForm.boar2Name || ''} onChange={(e) => updBreed('boar2Name', e.target.value)} />
                </div>
              </div>
            </div>

            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10}}>
              <div>
                <label style={S.label}>Sow group{editBreedId ? ' (fixed)' : ''}</label>
                <select
                  value={breedForm.group}
                  onChange={(e) => updBreed('group', e.target.value)}
                  disabled={!!editBreedId}
                >
                  {PIG_GROUPS.map((g) => (
                    <option key={g} value={g}>
                      Group {g}
                    </option>
                  ))}
                </select>
                {!editBreedId && (
                  <div style={{fontSize: 13, color: 'var(--ink)', marginTop: 4}}>
                    Group is fixed once the cycle is added.
                  </div>
                )}
              </div>
              <div>
                <label style={S.label}>Batch number</label>
                <input
                  type="text"
                  value={breedForm.customSuffix || ''}
                  onChange={(e) => updBreed('customSuffix', e.target.value)}
                  placeholder="e.g. 26-01"
                />
              </div>
              <div>
                <label style={S.label}>First day exposure (start date)</label>
                <input
                  type="date"
                  value={breedForm.exposureStart}
                  onChange={(e) => updBreed('exposureStart', e.target.value)}
                />
              </div>
            </div>

            {/* Sow-pool banner — shows how many sows are assigned to
                    the current group in the breeders tab. Prompts admin to
                    set up groups there if empty. */}
            {(() => {
              const pool = sowsForGroup(breedForm.group);
              const locked = isCycleLocked(breedForm.exposureStart);
              if (locked) {
                return (
                  <div
                    style={{
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border-strong)',
                      borderRadius: 10,
                      padding: '8px 12px',
                      fontSize: 12,
                      color: 'var(--ink)',
                    }}
                  >
                    Cycle locked — started on {fmtMDY(breedForm.exposureStart)} more than 2 weeks ago. Sow-boar
                    assignments can't be changed.
                  </div>
                );
              }
              if (pool.length === 0) {
                return (
                  <div
                    style={{
                      background: 'var(--warn-soft)',
                      border: '1px solid var(--border)',
                      borderRadius: 10,
                      padding: '8px 12px',
                      fontSize: 12,
                      color: 'var(--warn-ink)',
                    }}
                  >
                    No sows assigned to Group {breedForm.group} yet. Go to the <strong>Breeding Pigs</strong> tab and
                    set each sow's group to auto-fill this cycle.
                  </div>
                );
              }
              return (
                <div
                  style={{
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border)',
                    borderRadius: 10,
                    padding: '8px 12px',
                    fontSize: 12,
                    color: 'var(--text-primary)',
                  }}
                >
                  <strong>{pool.length} sows</strong> currently in Group {breedForm.group}. Click a chip's × to unassign
                  a sow; assignments lock 2 weeks after the cycle starts.
                </div>
              );
            })()}

            {(() => {
              const parseTags = (str: string | null | undefined) =>
                (str || '')
                  .split('\n')
                  .map((t) => t.trim())
                  .filter(Boolean);
              const b1List = parseTags(breedForm.boar1Tags);
              const b2List = parseTags(breedForm.boar2Tags);
              const locked = isCycleLocked(breedForm.exposureStart);
              const assigned = new Set([...b1List, ...b2List]);
              const available = sowsForGroup(breedForm.group)
                .filter((t) => !assigned.has(t))
                .sort((a, b) => (parseFloat(a) || 0) - (parseFloat(b) || 0));
              // Multi-field setter that mirrors updBreed's autosave behavior
              // so we can update boarNTags AND excludedSows in one shot — now
              // through the same confirmed-draft controller.
              function setFormFields(updates: Partial<BreedForm>) {
                const next = {...breedForm, ...updates};
                setBreedForm(next);
                if (next.exposureStart) breedDraft.update(next);
              }
              function removeSow(boarKey: BoarTagKey, tag: string) {
                const cur = parseTags(breedForm[boarKey]);
                const curExcluded = Array.isArray(breedForm.excludedSows) ? breedForm.excludedSows : [];
                const nextExcluded = curExcluded.includes(tag) ? curExcluded : [...curExcluded, tag];
                setFormFields({[boarKey]: cur.filter((t) => t !== tag).join('\n'), excludedSows: nextExcluded});
              }
              function addSow(boarKey: BoarTagKey, tag: string) {
                if (!tag) return;
                const cur = parseTags(breedForm[boarKey]);
                if (cur.includes(tag)) return;
                const curExcluded = Array.isArray(breedForm.excludedSows) ? breedForm.excludedSows : [];
                setFormFields({
                  [boarKey]: [...cur, tag].join('\n'),
                  excludedSows: curExcluded.filter((t) => t !== tag),
                });
              }
              const chipRow = (list: readonly string[], boarKey: BoarTagKey) => (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 4,
                    minHeight: 30,
                    padding: '6px 8px',
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    borderRadius: 10,
                  }}
                >
                  {list.length === 0 && (
                    <span style={{fontSize: 11, color: 'var(--ink-faint)', fontStyle: 'italic', padding: '2px 0'}}>
                      No sows assigned
                    </span>
                  )}
                  {list.map((t) => (
                    <span
                      key={t}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        background: 'var(--surface)',
                        border: '1px solid var(--border-strong)',
                        borderRadius: 12,
                        padding: '2px 4px 2px 10px',
                        fontSize: 12,
                        fontWeight: 600,
                        color: 'var(--ink)',
                      }}
                    >
                      #{t}
                      {!locked && (
                        <Button onClick={() => removeSow(boarKey, t)} title="Remove">
                          ×
                        </Button>
                      )}
                    </span>
                  ))}
                </div>
              );
              const addDropdown = (boarKey: BoarTagKey) =>
                !locked && (
                  <div style={{marginTop: 4}}>
                    <select
                      onChange={(e) => {
                        addSow(boarKey, e.target.value);
                        e.target.value = '';
                      }}
                      style={{fontSize: 11}}
                    >
                      <option value="">＋ Add sow from Group {breedForm.group}...</option>
                      {available.map((t) => (
                        <option key={t} value={t}>
                          #{t}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              return (
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10}}>
                  <div>
                    <label style={S.label}>
                      {breedForm.boar1Name || boarNames.boar1} sow tags{' '}
                      <span style={{fontWeight: 400, color: 'var(--ink-faint)'}}>({b1List.length})</span>
                    </label>
                    {chipRow(b1List, 'boar1Tags')}
                    {addDropdown('boar1Tags')}
                  </div>
                  <div>
                    <label style={S.label}>
                      {breedForm.boar2Name || boarNames.boar2} sow tags{' '}
                      <span style={{fontWeight: 400, color: 'var(--ink-faint)'}}>({b2List.length})</span>
                    </label>
                    {chipRow(b2List, 'boar2Tags')}
                    {addDropdown('boar2Tags')}
                  </div>
                </div>
              );
            })()}
            <div
              style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '8px 12px',
                fontSize: 12,
                color: 'var(--text-primary)',
              }}
            >
              <strong>
                Total sows in cycle:{' '}
                {(breedForm.boar1Tags || '')
                  .split('\n')
                  .map((t) => t.trim())
                  .filter(Boolean).length +
                  (breedForm.boar2Tags || '')
                    .split('\n')
                    .map((t) => t.trim())
                    .filter(Boolean).length}
              </strong>
              &nbsp;·&nbsp; {boarNames.boar1}:{' '}
              {
                (breedForm.boar1Tags || '')
                  .split('\n')
                  .map((t) => t.trim())
                  .filter(Boolean).length
              }
              &nbsp;·&nbsp; {boarNames.boar2}:{' '}
              {
                (breedForm.boar2Tags || '')
                  .split('\n')
                  .map((t) => t.trim())
                  .filter(Boolean).length
              }
            </div>

            {/* Calculated timeline preview */}
            {breedForm.exposureStart &&
              (() => {
                const tl = calcBreedingTimeline(breedForm.exposureStart);
                if (!tl) return null;
                return (
                  <div
                    style={{
                      background: 'var(--bg-card)',
                      border: '1px solid var(--border)',
                      borderRadius: 10,
                      padding: '10px 14px',
                      fontSize: 12,
                      color: 'var(--text-primary)',
                    }}
                  >
                    <div style={{fontWeight: 600, marginBottom: 6}}>Calculated timeline</div>
                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 12px'}}>
                      <div>
                        Sows in with Boars:{' '}
                        <strong>
                          {fmtMDY(tl.boarStart)} → {fmtMDY(tl.boarEnd)}
                        </strong>
                      </div>
                      <div>
                        Sows in Exposed Paddock:{' '}
                        <strong>
                          {fmtMDY(tl.paddockStart)} → {fmtMDY(tl.paddockEnd)}
                        </strong>
                      </div>
                      <div>
                        Farrowing window:{' '}
                        <strong>
                          {fmtMDY(tl.farrowingStart)} → {fmtMDY(tl.farrowingEnd)}
                        </strong>
                      </div>
                      <div>
                        Weaning:{' '}
                        <strong>
                          {fmtMDY(tl.weaningStart)} → {fmtMDY(tl.weaningEnd)}
                        </strong>
                      </div>
                      <div>
                        Grow-out ends: <strong>{fmtMDY(tl.growEnd)}</strong>
                      </div>
                    </div>
                  </div>
                );
              })()}

            <div>
              <label style={S.label}>Notes</label>
              <textarea value={breedForm.notes} onChange={(e) => updBreed('notes', e.target.value)} rows={2} />
            </div>
          </div>
        </Modal>
      )}

      <div style={{padding: '1rem'}}>
        {/* Nav bar */}
        <div style={{display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12, alignItems: 'center'}}>
          <Button
            onClick={() => {
              setBreedTlStart(pigBreedingTimelineRange(todayISO()).start);
              setTodayScrollRequest((request) => request + 1);
            }}
            title="Show today; earlier history remains available to the left"
          >
            Today
          </Button>
          <span style={{fontSize: 11, color: 'var(--ink-faint)'}}>
            {fmtMDY(timelineRange.start)} — {fmtMDY(timelineRange.end)}
          </span>
          <Button
            variant="primary"
            onClick={() => {
              // An unconfirmed edit draft keeps its modal (E4): creating a
              // cycle while the editor holds a refused draft would strand it.
              if (breedDraft.isDirty()) {
                setShowBreedForm(true);
                return;
              }
              setBreedForm({
                ...EMPTY_BREED,
                excludedSows: [],
                boar1Tags: mergeSowsIntoB1(EMPTY_BREED.group, '', '', []),
              });
              setEditBreedId(null);
              setShowBreedForm(true);
            }}
            style={{marginLeft: 'auto'}}
          >
            + Add Cycle
          </Button>
        </div>

        {/* Gantt */}
        <div
          ref={ganttRef}
          className="breed-gantt"
          role="region"
          aria-label="Pig breeding timeline"
          tabIndex={0}
          data-pig-timeline-start={timelineRange.start}
          data-pig-timeline-end={timelineRange.end}
          style={{...S.card, overflowX: 'auto', position: 'relative'}}
        >
          {/* Floating tooltip for pig timeline */}
          {tooltip &&
            tooltip.type === 'pig' &&
            (function () {
              return React.createElement(
                'div',
                {
                  style: {
                    position: 'fixed',
                    left: tooltip.vx,
                    top: tooltip.vy,
                    transform: 'translate(-50%,-100%)',
                    zIndex: 300,
                    pointerEvents: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  },
                },
                React.createElement(
                  'div',
                  {
                    style: {
                      background: 'var(--surface)',
                      color: 'var(--ink)',
                      border: '1px solid var(--border)',
                      borderRadius: 12,
                      padding: '9px 12px',
                      fontSize: 12,
                      boxShadow: '0 7px 20px rgba(20,30,40,.10)',
                      minWidth: 180,
                      maxWidth: 260,
                    },
                  },
                  React.createElement(
                    'div',
                    {style: {fontWeight: 700, fontSize: 13, marginBottom: 5, color: 'var(--ink)'}},
                    (tooltip.cycleLbl || 'Group ' + tooltip.group) + ' \u2014 ' + tooltip.phaseName,
                  ),
                  React.createElement(
                    'div',
                    {style: {display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 10px'}},
                    React.createElement('span', {style: {color: 'var(--label)'}}, 'Period:'),
                    React.createElement(
                      'span',
                      {style: {color: 'var(--ink)'}},
                      fmtMDY(tooltip.start) + ' \u2192 ' + fmtMDY(tooltip.end),
                    ),
                    React.createElement('span', {style: {color: 'var(--label)'}}, 'Sows:'),
                    React.createElement('span', {style: {color: 'var(--ink)'}}, (tooltip.sowCount || '?') + ' total'),
                  ),
                ),
                React.createElement('div', {
                  style: {
                    width: 0,
                    height: 0,
                    borderLeft: '7px solid transparent',
                    borderRight: '7px solid transparent',
                    borderTop: '7px solid var(--surface)',
                  },
                }),
              );
            })()}
          <div style={{width: `${210 + gridWidth}px`}}>
            {/* Week headers */}
            <div style={{display: 'flex', borderBottom: '1px solid var(--border)'}}>
              <div
                style={{
                  width: 210,
                  flexShrink: 0,
                  padding: '6px 10px',
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--ink-faint)',
                  borderRight: '1px solid var(--border)',
                  background: 'var(--surface-2)',
                  position: 'sticky',
                  left: 0,
                  zIndex: 10,
                }}
              >
                Phase
              </div>
              <div style={{flex: 1, position: 'relative', height: 26, overflow: 'hidden'}}>
                {bWkHdrs.map((w, i) => {
                  const isNew = w.getDate() <= 7;
                  return (
                    <div
                      key={i}
                      data-pig-timeline-week={toISO(w)}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: `${bpct(toISO(w))}%`,
                        height: '100%',
                        borderLeft: `1px solid ${isNew ? 'var(--border-strong)' : 'var(--hairline)'}`,
                        paddingLeft: 3,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <span
                        style={{
                          fontSize: 9,
                          whiteSpace: 'nowrap',
                          color: isNew ? 'var(--text-2)' : 'var(--label)',
                          fontWeight: isNew ? 600 : 400,
                        }}
                      >
                        {isNew ? w.toLocaleDateString('en-US', {month: 'short', year: 'numeric'}) : w.getDate()}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {phaseRows.map((row, ri) => {
              const isGroupStart = ri % 6 === 0;
              const rowCycles = breedingCycles.filter((c) => c.group === row.group);
              const todayPct = bpct(timelineToday);
              const phaseColor = `var(--group-${(PIG_GROUPS.indexOf(row.group as (typeof PIG_GROUPS)[number]) % 6) + 1})`;
              const txtColor = 'white';
              return (
                <div
                  key={ri}
                  style={{
                    display: 'flex',
                    borderTop: isGroupStart ? '2px solid var(--border-strong)' : '1px solid var(--hairline)',
                  }}
                >
                  <div
                    style={{
                      width: 210,
                      flexShrink: 0,
                      padding: '0 10px',
                      display: 'flex',
                      alignItems: 'center',
                      height: ganttRowHeight,
                      fontSize: 10,
                      fontWeight: isGroupStart ? 700 : 400,
                      color: isGroupStart ? 'var(--ink)' : 'var(--text-2)',
                      borderRight: '1px solid var(--border)',
                      background: 'var(--surface-2)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      position: 'sticky',
                      left: 0,
                      zIndex: 10,
                    }}
                  >
                    {row.label}
                  </div>
                  <div style={{flex: 1, position: 'relative', height: ganttRowHeight}}>
                    {bWkHdrs.map((week, i) => (
                      <div
                        key={i}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: `${bpct(toISO(week))}%`,
                          height: '100%',
                          borderLeft: '1px solid var(--hairline)',
                          pointerEvents: 'none',
                        }}
                      />
                    ))}
                    {todayPct >= 0 && todayPct <= 100 && (
                      <div
                        data-pig-timeline-today="1"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: `${todayPct}%`,
                          height: '100%',
                          borderLeft: '2px solid rgba(24,122,84,.4)',
                          pointerEvents: 'none',
                          zIndex: 2,
                        }}
                      />
                    )}
                    {rowCycles.map((c) => {
                      // Always recalculate live so stale stored values never show
                      const liveTl = calcBreedingTimeline(c.exposureStart);
                      if (!liveTl) return null;
                      const liveC = {...c, ...liveTl};
                      const s = liveC[row.startKey],
                        e = liveC[row.endKey];
                      if (!s || !e) return null;
                      const left = Math.max(0, bpct(s));
                      const right = Math.min(100, bpct(e));
                      const w = right - left;
                      if (w < 0.2) return null;
                      const phaseNames = {
                        boar: 'Sows in with Boars',
                        paddock: 'Sows in Exposed Paddock',
                        farrowing: 'Farrowing',
                        weaning: 'Weaning',
                        gilt: 'Gilts Grow Out',
                        boarGrow: 'Boars Grow Out',
                      };
                      const cLbl = cycleLabel(c, cycleSeqMap);
                      // customSuffix (admin override) wins over the auto YY-NN
                      // so bar labels reflect what you typed into the modal.
                      const cSuffix = (c.customSuffix && String(c.customSuffix).trim()) || cycleSeqMap[c.id as string];
                      const label = `G${c.group}${cSuffix ? ' · ' + cSuffix : ''} — ${phaseNames[row.phase] || row.phase}`;
                      return (
                        <div
                          key={c.id}
                          data-pig-timeline-cycle={c.id}
                          data-pig-timeline-phase={row.phase}
                          className="pig-timeline-bar"
                          role="button"
                          tabIndex={0}
                          aria-label={label}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              event.currentTarget.click();
                            }
                          }}
                          onClick={() => openSavedCycle(c)}
                          onMouseEnter={function (ev) {
                            var r = ev.currentTarget.getBoundingClientRect();
                            setTooltip({
                              type: 'pig',
                              group: c.group,
                              cycleLbl: cLbl,
                              phase: row.phase,
                              phaseName: phaseNames[row.phase] || row.phase,
                              start: s,
                              end: e,
                              sowCount: c.sowCount,
                              vx: r.left + r.width / 2,
                              vy: r.top - 10,
                            });
                          }}
                          onMouseLeave={function () {
                            setTooltip(null);
                          }}
                          style={{
                            position: 'absolute',
                            left: `${left}%`,
                            width: `${w}%`,
                            top: 4,
                            bottom: 4,
                            borderRadius: 10,
                            cursor: 'pointer',
                            background: phaseColor,
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 7px',
                            overflow: 'hidden',
                            outline:
                              tooltip &&
                              tooltip.type === 'pig' &&
                              tooltip.group === c.group &&
                              tooltip.phase === row.phase
                                ? '2px solid rgba(0,0,0,.25)'
                                : 'none',
                          }}
                        >
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 600,
                              color: txtColor,
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                            }}
                          >
                            {label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        <div style={{display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap', alignItems: 'center'}}>
          {PIG_GROUPS.map((group, index) => (
            <span
              key={group}
              style={{display: 'inline-flex', alignItems: 'center', gap: 7, color: 'var(--text-2)', fontSize: 13}}
            >
              <span
                aria-hidden="true"
                style={{width: 7, height: 7, borderRadius: '50%', background: `var(--group-${(index % 6) + 1})`}}
              />
              Group {group}
            </span>
          ))}
          <span style={{fontSize: 12, color: 'var(--text-2)'}}>Open a bar to edit · Green line = today</span>
        </div>

        {/* Cycle cards below chart */}
        {breedingCycles.length > 0 && (
          <div className="pig-cycle-cards">
            <h2>Breeding cycles</h2>
            {breedingCycles
              .slice()
              .sort((a, b) => (b.exposureStart || '').localeCompare(a.exposureStart || ''))
              .map((c) => {
                const tl = calcBreedingTimeline(c.exposureStart) || c;
                const cycleStatus = calcCycleStatus(c);
                const boarAssignments = [
                  {key: 'first', name: c.boar1Name || boarNames.boar1, tags: c.boar1Tags},
                  {key: 'second', name: c.boar2Name || boarNames.boar2, tags: c.boar2Tags},
                ].map((boar) => ({
                  ...boar,
                  tags: (boar.tags || '')
                    .split(/[\n,]/)
                    .map((tag) => tag.trim())
                    .filter(Boolean),
                }));
                const openEdit = () => openSavedCycle(c);
                return (
                  <div key={c.id} {...openableProps(openEdit)} className="pig-cycle-card wcf-clickable">
                    <div className="pig-cycle-card-head">
                      <span className="pig-cycle-card-title">
                        <span style={programDotStyle('pig')} />
                        <strong>{cycleLabel(c, cycleSeqMap)}</strong>
                      </span>
                      <span className="pig-cycle-pill pig-cycle-pill--count">{c.sowCount ?? '?'} sows total</span>
                      <Badge status={cycleStatus}>{cycleStatus.charAt(0).toUpperCase() + cycleStatus.slice(1)}</Badge>
                    </div>
                    <div className="pig-cycle-card-dates">
                      <span>
                        <span className="pig-cycle-date-label">Sows in with Boars</span>
                        <strong>
                          {fmtMDY(tl.boarStart)} – {fmtMDY(tl.boarEnd)}
                        </strong>
                      </span>
                      <span>
                        <span className="pig-cycle-date-label">Exposed Paddock</span>
                        <strong>
                          {fmtMDY(tl.paddockStart)} – {fmtMDY(tl.paddockEnd)}
                        </strong>
                      </span>
                      <span>
                        <span className="pig-cycle-date-label">Farrowing</span>
                        <strong>
                          {fmtMDY(tl.farrowingStart)} – {fmtMDY(tl.farrowingEnd)}
                        </strong>
                      </span>
                      <span>
                        <span className="pig-cycle-date-label">Grow-out ends</span>
                        <strong>{fmtMDY(tl.growEnd)}</strong>
                      </span>
                    </div>
                    <div className="pig-cycle-boars">
                      {boarAssignments.map((boar) => (
                        <div key={boar.key} className={`pig-cycle-boar pig-cycle-boar--${boar.key}`}>
                          <div className="pig-cycle-boar-head">
                            <strong>{boar.name}</strong>
                            <span className="pig-cycle-pill pig-cycle-pill--count">
                              {boar.tags.length} {boar.tags.length === 1 ? 'sow' : 'sows'}
                            </span>
                          </div>
                          {boar.tags.length > 0 ? (
                            <ul className="pig-cycle-sow-tags" aria-label={`${boar.name} assigned sows`}>
                              {boar.tags.map((tag, index) => (
                                <li key={`${tag}-${index}`}>{tag.startsWith('#') ? tag : `#${tag}`}</li>
                              ))}
                            </ul>
                          ) : (
                            <p className="pig-cycle-boar-empty">No sows assigned</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
        {/* Three states where there used to be one. The cycle list arrived with
            the boot payload, so "empty" and "not loaded yet" were the same
            frame and "unreachable" could not happen. A route-local read makes
            all three real, and reporting a pending or failed read as "No
            breeding cycles yet — click + Add Cycle" is worse than merely wrong
            here: it invites a duplicate cycle for a group that already has one,
            which is the exact defect the cattle breeding localization closed. */}
        {breedingResource.loading && breedingCycles.length === 0 && (
          <div
            data-pig-breeding-loading
            style={{textAlign: 'center', padding: '3rem', color: 'var(--ink-faint)', fontSize: 13}}
          >
            Loading breeding cycles…
          </div>
        )}
        {/* Boolean(), not a bare truthiness chain: `error` is `unknown` on the
            resource contract, so the && expression would be `unknown` and could
            not be a ReactNode. */}
        {!breedingResource.loading && Boolean(breedingResource.error) && (
          <div
            data-pig-breeding-load-error
            style={{textAlign: 'center', padding: '3rem', color: 'var(--danger-ink)', fontSize: 13}}
          >
            Could not load breeding cycles.{' '}
            <Button type="button" onClick={() => breedingResource.refetch()}>
              Retry
            </Button>
          </div>
        )}
        {!breedingResource.loading && !breedingResource.error && breedingCycles.length === 0 && (
          <div style={{textAlign: 'center', padding: '3rem', color: 'var(--ink-faint)', fontSize: 13}}>
            No breeding cycles yet — click "+ Add Cycle" to get started
          </div>
        )}
      </div>
    </div>
  );
}
